import { Dropdown, Tooltip, useDrawer } from '@iveco-ti/iveco-ds'
import { FileEdit, FileSearch, LayoutGrid, Trash } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Can } from '@/common/components/Can'
import { usePermissions } from '@/common/hooks/usePermissions'
import { useInactivateAccount } from '../hooks/useInactivateAccount'
import { AccountPaginated } from '../hooks/usePaginateAccounts'
import { useSelectedAccountStore } from '../stores/useSelectedAccountStore'

type AccountActiveActionsProps = {
  account: AccountPaginated
}

export function AccountActiveActions({ account }: AccountActiveActionsProps) {
  const { t } = useTranslation(['common', 'sso'])
  const { can } = usePermissions()
  const drawerEditAccount = useDrawer('drawer-edit-account')
  const drawerViewAccount = useDrawer('drawer-view-account')
  const drawerManageApplications = useDrawer('drawer-manage-applications')
  const { selectAccount } = useSelectedAccountStore()
  const { handleInactivateAccount } = useInactivateAccount()

  const canManageApplications = can('manage_applications', 'Account')
  const canInactivate = can('inactivate', 'Account')

  const hasAnyPermission = canManageApplications || canInactivate

  return (
    <div className="flex flex-row gap-2 items-center justify-center">
      <Tooltip text={t('common:components.table_row.view')}>
        <button
          type="button"
          className="p-1 hover:bg-base-100 rounded"
          onClick={() => {
            selectAccount(account)
            drawerViewAccount.open()
          }}
        >
          <FileSearch size={18} className="text-brand-700" />
        </button>
      </Tooltip>

      <Can I="edit" a="Account">
        <Tooltip text={t('common:components.table_row.edit')}>
          <button
            type="button"
            className="p-1 hover:bg-base-100 rounded"
            onClick={() => {
              selectAccount(account)
              drawerEditAccount.open()
            }}
          >
            <FileEdit size={18} className="text-brand-700" />
          </button>
        </Tooltip>
      </Can>

      {hasAnyPermission && (
        <Dropdown.Root>
          <Dropdown.Trigger className="text-brand-700" />

          <Dropdown.Body>
            <Dropdown.List>
              {canManageApplications && (
                <Dropdown.Item
                  onClick={() => {
                    selectAccount(account)
                    drawerManageApplications.open()
                  }}
                  className="text-brand-700 text-sm font-normal"
                >
                  <Dropdown.Icon icon={<LayoutGrid />} />
                  {t('sso:accounts.table.table_row.dropdown.manage')}
                </Dropdown.Item>
              )}

              {canInactivate && (
                <Dropdown.Item
                  onClick={() => handleInactivateAccount(account.id)}
                  className="text-danger-700 text-sm font-normal"
                >
                  <Dropdown.Icon icon={<Trash />} />
                  {t('sso:accounts.table.table_row.dropdown.remove')}
                </Dropdown.Item>
              )}
            </Dropdown.List>
          </Dropdown.Body>
        </Dropdown.Root>
      )}
    </div>
  )
}
